import React, { Component } from 'react';
import ReactDOMServer from 'react-dom/server';
import {
  Backdrop,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Grid,
  Snackbar,
  Tooltip,
  Paper,
  List,
  ListItem,
  Select,
  MenuItem,
  Typography,
} from '@material-ui/core';
import {
  formatDateOnlyStringFromLocalTime,
  isBeforeToday,
} from '../helpers/helpers';
import {
  TYPE_PLANNING_PRODUCING,
  TYPE_MERCHANDISE_COORDINATION,
  TYPE_POIRIER_SET,
} from '../constants/booking';
import {
  CallSheetEmailStatus,
  positionGroups,
  producerMapping,
  shouldSendCallSheetStatus,
} from '../constants/callsheet';
import { buildCallsheetsToSend } from '../helpers/callSheetHelpers';
import {
  fetchCallSheetData,
  sendCallSheets,
  fetchLocationTypes,
} from '../helpers/externalRequests';
import i18n from '../i18n';
import DateSelector from '../components/Callsheet/DateSelector';
import VisualProductionCallsheet from '../components/Callsheet/VisualProductionCallsheet';
import ContentLocalizationCallsheet from '../components/Callsheet/ContentLocalizationCallsheet';
import ManagementCallsheet from '../components/Callsheet/ManagementCallsheet';
import '../assets/stylesheets/CallsheetView.css';
import RequireScope from '../components/RequireScope';
import EverythingElseCallsheet from '../components/Callsheet/EverythingElseCallsheet';

const AUTOHIDE_DURATION = 5000;
const IN_PROGRESS_REFRESH = 3000;

class CallsheetView extends Component {
  constructor(props) {
    super(props);

    this.employees = {};
    this.progressUpdateTimeoutId = null;

    this.state = {
      bookings: null,
      openBackdrop: false,
      shouldSendCallSheet: [],
      selectedDate: new Date(),
      selectedCallsheet: 'management',
      snackOpen: false,
      snackbarMessage: null,
    };
  }

  componentDidMount() {
    const { selectedDate } = this.state;
    this.fetchBookings(selectedDate);
  }

  onDateChange = (selectedDate) => {
    this.setState({
      selectedDate,
    });
    this.fetchBookings(selectedDate);
  };

  onCallsheetChange = (event) => {
    const selectedCallsheet = event.target.value;
    this.setState({ selectedCallsheet });
  };

  buildShouldSendCallSheet = (bookings) => {
    const shouldSendCallSheet = [];
    if (bookings) {
      Object.values(bookings).forEach((locationType) => {
        Object.values(locationType).forEach((locationValue) => {
          Object.values(locationValue.positions).forEach((positionData) => {
            positionData.employees.forEach((employee) => {
              shouldSendCallSheet[employee.id] =
                shouldSendCallSheetStatus.includes(employee.callSheetEmailSent);
            });
          });
        });
      });
    }
    return shouldSendCallSheet;
  };

  addRequiredPositonData = (bookings, locationTypes) => {
    return Object.entries(bookings).reduce((res, locationTypeEntry) => {
      const [locationTypeIdString, locations] = locationTypeEntry;
      const locationTypeId = parseInt(locationTypeIdString, 10);
      res[locationTypeIdString] = Object.entries(locations).reduce(
        (alteredLocations, locationEntry) => {
          const [locationName, locationData] = locationEntry;
          const locationType = locationTypes.find(
            (locType) => locType.id === locationTypeId
          );
          const requiredPositions = locationType.requiredPositions.sort(
            (a, b) =>
              a.group === b.group ? a.order - b.order : a.group - b.group
          );
          // eslint-disable-next-line no-param-reassign
          alteredLocations[locationName] = {
            ...locationData,
            requiredPositions,
          };
          return alteredLocations;
        },
        {}
      );
      return res;
    }, {});
  };

  areAnyCallSheetEmailsInProgress = (bookings) => {
    if (bookings) {
      return !!Object.values(bookings).find((locationType) =>
        Object.values(locationType).find((locationValue) =>
          Object.values(locationValue.positions).find((positionData) =>
            positionData.employees.some(
              (employee) =>
                employee.callSheetEmailSent === CallSheetEmailStatus.IN_PROGRESS
            )
          )
        )
      );
    }
    return false;
  };

  fetchBookings = async (selectedDate) => {
    try {
      const locationTypesPromise = fetchLocationTypes();
      const date = formatDateOnlyStringFromLocalTime(selectedDate);
      const bookingsPromise = fetchCallSheetData(date);

      const bookings = this.addRequiredPositonData(
        (await bookingsPromise).data,
        (await locationTypesPromise).data
      );
      const shouldSendCallSheet = this.buildShouldSendCallSheet(bookings);
      const sendingInProgress = this.areAnyCallSheetEmailsInProgress(bookings);
      if (sendingInProgress) {
        if (this.progressUpdateTimeoutId) {
          clearTimeout(this.progressUpdateTimeoutId);
        }
        this.progressUpdateTimeoutId = setTimeout(
          () => this.fetchBookings(selectedDate),
          IN_PROGRESS_REFRESH
        );
        return this.setState({
          bookings,
          shouldSendCallSheet,
          openBackdrop: true,
        });
      }
      if (this.progressUpdateTimeoutId) {
        clearTimeout(this.progressUpdateTimeoutId);
        this.progressUpdateTimeoutId = null;
        return this.setState({
          bookings,
          shouldSendCallSheet,
          openBackdrop: false,
        });
      }

      return this.setState({
        bookings,
        shouldSendCallSheet,
      });
    } catch (e) {
      return this.setState({
        snackOpen: true,
        snackbarMessage: (e && e.message) || i18n.t('callsheetFetchError'),
      });
    }
  };

  displaySnackbar = (message) => {
    this.setState({
      snackOpen: true,
      snackbarMessage: message,
    });
  };

  hideSnackbar = () => {
    this.setState({ snackOpen: false });
  };

  getCallSheet = (bookings, selectedDate, locationContacts, positionName) => {
    if (positionGroups.visualProduction.includes(positionName)) {
      return ReactDOMServer.renderToString(
        <VisualProductionCallsheet
          bookings={bookings}
          locationContacts={locationContacts}
          date={selectedDate}
          renderForEmail
        />
      );
    }
    if (positionGroups.contentLocalization.includes(positionName)) {
      return ReactDOMServer.renderToString(
        <ContentLocalizationCallsheet
          bookings={bookings}
          locationContacts={locationContacts}
          date={selectedDate}
          renderForEmail
        />
      );
    }
    if (positionGroups.everythingElse.includes(positionName)) {
      return ReactDOMServer.renderToString(
        <EverythingElseCallsheet
          bookings={bookings}
          locationContacts={locationContacts}
          date={selectedDate}
          renderForEmail
        />
      );
    }
    if (positionGroups.management.includes(positionName)) {
      return ReactDOMServer.renderToString(
        <ManagementCallsheet
          bookings={bookings}
          locationContacts={locationContacts}
          date={selectedDate}
          renderForEmail
        />
      );
    }
    return null;
  };

  getEmployees = (bookings) => {
    const employees = new Set();
    if (bookings) {
      Object.entries(bookings).forEach((locationTypeEntry) => {
        const [locationTypeId, locationTypeValues] = locationTypeEntry;
        Object.entries(locationTypeValues).forEach((locationEntry) => {
          const [locationName, locationValues] = locationEntry;
          Object.entries(locationValues.positions).forEach((positionEntry) => {
            const [positionName, positionData] = positionEntry;
            positionData.employees.forEach((employee) => {
              const employeePositionData = {
                positionName,
                locationTypeId,
                locationName,
                ...employee,
              };
              employees.add(employeePositionData);
            });
          });
        });
      });
    }
    this.employees = employees;
    return Array.from(employees);
  };

  generateLocationContacts = (bookings, producerMap) => {
    if (!bookings) {
      return null;
    }
    const producers = bookings[TYPE_PLANNING_PRODUCING]
      ? Object.values(bookings[TYPE_PLANNING_PRODUCING])[0].positions
      : {};
    const coordinators = bookings[TYPE_MERCHANDISE_COORDINATION]
      ? Object.values(bookings[TYPE_MERCHANDISE_COORDINATION])[0].positions
      : {};
    const eeCoordinators = bookings[TYPE_POIRIER_SET]
      ? Object.values(bookings[TYPE_POIRIER_SET])[0].positions
      : {};
    const producersAndCordinators = {
      ...producers,
      ...coordinators,
      ...eeCoordinators,
    };
    const locationContacts = {};
    Object.entries(producerMap).forEach((producerMapEntry) => {
      const [locationTypeId, producerPositionNames] = producerMapEntry;
      if (Array.isArray(producerPositionNames)) {
        // There are multiple producers
        locationContacts[locationTypeId] = [];
        producerPositionNames.forEach((producerId) => {
          if (
            producersAndCordinators[producerId] &&
            producersAndCordinators[producerId].employees.length > 0
          ) {
            locationContacts[locationTypeId].push(
              producersAndCordinators[producerId].employees[0].name
            );
          } else {
            locationContacts[locationTypeId].push(i18n.t('notAvailable'));
          }
        });
      } else if (
        producersAndCordinators[producerPositionNames] &&
        producersAndCordinators[producerPositionNames].employees.length > 0
      ) {
        // There is a single producer
        locationContacts[locationTypeId] =
          producersAndCordinators[producerPositionNames].employees[0].name;
      } else {
        locationContacts[locationTypeId] = i18n.t('notAvailable');
      }
    });
    return locationContacts;
  };

  getCallsheetPreview = (
    bookings,
    date,
    locationContacts,
    selectedCallsheet
  ) => {
    if (bookings && Object.keys(bookings).length > 0) {
      switch (selectedCallsheet) {
        case 'visualProduction':
          return (
            <VisualProductionCallsheet
              bookings={bookings}
              date={date}
              locationContacts={locationContacts}
              renderForEmail={false}
            />
          );
        case 'contentLocalization':
          return (
            <ContentLocalizationCallsheet
              bookings={bookings}
              date={date}
              locationContacts={locationContacts}
              renderForEmail={false}
            />
          );
        case 'everythingElse':
          return (
            <EverythingElseCallsheet
              bookings={bookings}
              date={date}
              locationContacts={locationContacts}
              renderForEmail={false}
            />
          );
        case 'management':
          return (
            <ManagementCallsheet
              bookings={bookings}
              date={date}
              locationContacts={locationContacts}
              renderForEmail={false}
            />
          );
        default:
          console.error(
            `No matching callsheet found: ${JSON.stringify(selectedCallsheet)}`
          );
      }
    }
    return <div />; // If there is nothing to be displayed (no bookings) return an empty component
  };

  handleShouldSendEmail = (event) => {
    event.persist();
    const id = parseInt(event.currentTarget.name, 10);
    this.setState((prevState) => {
      const shouldSendCallSheet = [...prevState.shouldSendCallSheet];
      shouldSendCallSheet[id] = !prevState.shouldSendCallSheet[id];
      return { shouldSendCallSheet };
    });
  };

  sendCallSheetCallback = async (params) => {
    const { bookings, selectedDate, locationContacts, shouldSendCallSheet } =
      params;
    const employeesToSend = [...this.employees].filter(
      (employee) => shouldSendCallSheet[parseInt(employee.id, 10)]
    );
    const callsheetBuildResult = buildCallsheetsToSend(
      employeesToSend,
      bookings,
      selectedDate,
      locationContacts,
      this.getCallSheet
    );

    const noCallsheets = callsheetBuildResult.noCallsheets
      .map(
        (noCallsheet) =>
          ` ${noCallsheet.name}:${noCallsheet.positionName}:${noCallsheet.locationName} [${noCallsheet.email}]`
      )
      .toString();

    if (!callsheetBuildResult.callsheetsToSend.length) {
      if (callsheetBuildResult.noCallsheets.length) {
        return this.setState({
          openBackdrop: false,
          snackOpen: true,
          snackbarMessage: i18n.t('callsheet:noCallsheetsFound', {
            noCallsheets,
          }),
        });
      }
      return this.setState({
        openBackdrop: false,
        snackOpen: true,
        snackbarMessage: i18n.t('callsheet:noCallsheetsToSend'),
      });
    }

    try {
      const response = await sendCallSheets(
        callsheetBuildResult.callsheetsToSend
      );
      if (response.data?.successful?.length) {
        // we don't expect failed.length when async call sheet is enabled
        if (response.data.failed.length) {
          const failedEmails = response.data.failed.map(
            (failures) =>
              ` ${
                failures.params.employeeName
              } [${failures.recipients.main.toString()}]`
          );
          this.setState({
            openBackdrop: false,
            snackOpen: true,
            snackbarMessage: i18n.t('callsheet:emailSomeFailed', {
              failedEmails: failedEmails.toString(),
            }),
          });
        } else {
          this.setState({
            openBackdrop: false,
            snackOpen: true,
            snackbarMessage: response.data?.successful[0],
          });
        }
      } else {
        const errorMessage = response.data?.error
          ? `error message: ${response.data.error.message}`
          : `status code: ${response.status}`;
        this.setState({
          openBackdrop: false,
          snackOpen: true,
          snackbarMessage: i18n.t('callsheet:emailError', {
            errorMessage,
          }),
        });
        console.error(response);
      }
      if (callsheetBuildResult.noCallsheets.length) {
        setTimeout(
          () =>
            this.setState({
              openBackdrop: false,
              snackOpen: true,
              snackbarMessage: i18n.t('callsheet:noCallsheetsFound', {
                noCallsheets,
              }),
            }),
          AUTOHIDE_DURATION
        );
      }
      return this.fetchBookings(selectedDate);
    } catch (error) {
      console.error(error);
      this.setState({
        openBackdrop: false,
        snackOpen: true,
        snackbarMessage: i18n.t('callsheet:emailError', {
          errorMessage: error.message,
        }),
      });
      return this.fetchBookings(selectedDate);
    }
  };

  getCallSheetEmailStatusClassName = (callSheetEmailSentStatus) => {
    switch (callSheetEmailSentStatus) {
      case CallSheetEmailStatus.SENT:
        return 'emailConfirmed';
      case CallSheetEmailStatus.FAILED:
        return 'emailFailed';
      case CallSheetEmailStatus.IN_PROGRESS:
        return 'emailInProgress';
      default:
        return '';
    }
  };

  sendCallsheets = (bookings, selectedDate, locationContacts) => {
    const { shouldSendCallSheet } = this.state;
    this.setState({ openBackdrop: true }, () =>
      this.sendCallSheetCallback({
        bookings,
        selectedDate,
        locationContacts,
        shouldSendCallSheet,
      })
    );
  };

  render() {
    const {
      openBackdrop,
      selectedCallsheet,
      selectedDate,
      shouldSendCallSheet,
      snackOpen,
      snackbarMessage,
      bookings,
    } = this.state;

    const confirmedEmployees = this.getEmployees(bookings);
    const sendingEnabled = bookings && Object.keys(bookings).length > 0;
    const locationContacts = this.generateLocationContacts(
      bookings,
      producerMapping
    );

    const buttonDisabled = isBeforeToday(selectedDate);
    const toolTipTitle = buttonDisabled
      ? i18n.t('callsheet:cantSendPast')
      : i18n.t('callsheet:clickToSend');

    return (
      <div className="callsheet-container callsheetView">
        <Grid container>
          <Grid item xs={4}>
            <DateSelector
              onChangeSelectedDate={this.onDateChange}
              selectedDate={selectedDate}
            />
            <Paper className="blockContainer">
              <h5>{i18n.t('callsheet:selectCallsheetPreview')}</h5>
              <Select
                onChange={this.onCallsheetChange}
                value={selectedCallsheet}
              >
                <MenuItem value="visualProduction">
                  {i18n.t('callsheet:visualProduction')}
                </MenuItem>
                <MenuItem value="contentLocalization">
                  {i18n.t('callsheet:contentLocalization')}
                </MenuItem>
                <MenuItem value="everythingElse">
                  {i18n.t('callsheet:everythingElse')}
                </MenuItem>
                <MenuItem value="management">
                  {i18n.t('callsheet:management')}
                </MenuItem>
              </Select>
            </Paper>
            <Paper className="blockContainer">
              <h5>{i18n.t('callsheet:confirmedEmployees')}</h5>
              <List>
                {confirmedEmployees.length
                  ? confirmedEmployees.map((employee) => (
                    <ListItem
                      key={`${employee.id}`}
                      className={`listItem ${this.getCallSheetEmailStatusClassName(
                        employee.callSheetEmailSent
                      )}`}
                    >
                      <FormControlLabel
                        className="blackText"
                        control={
                          <Checkbox
                            checked={shouldSendCallSheet[employee.id]}
                            name={employee.id.toString()}
                            onChange={this.handleShouldSendEmail}
                            disabled={
                              buttonDisabled ||
                              employee.callSheetEmailSent ===
                                CallSheetEmailStatus.IN_PROGRESS
                            }
                          />
                        }
                        label={employee.name}
                      />
                    </ListItem>
                  ))
                  : i18n.t('callsheet:noEmployees')}
              </List>
              <RequireScope scopes={['callsheets:send']}>
                {sendingEnabled && (
                  <Tooltip
                    title={<Typography>{toolTipTitle}</Typography>}
                    arrow
                  >
                    {/** Span allows tooltips to display on disabled element */}
                    <span>
                      <Button
                        variant="contained"
                        className="ssenseButton ssensePrimary"
                        disabled={buttonDisabled}
                        onClick={() =>
                          this.sendCallsheets(
                            bookings,
                            selectedDate,
                            locationContacts
                          )
                        }
                      >
                        {i18n.t('callsheet:sendCallsheet')}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </RequireScope>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            {this.getCallsheetPreview(
              bookings,
              selectedDate,
              locationContacts,
              selectedCallsheet
            )}
          </Grid>

          <Snackbar
            message={snackbarMessage}
            open={snackOpen}
            onClose={this.hideSnackbar}
            autoHideDuration={AUTOHIDE_DURATION}
          />
        </Grid>
        <Backdrop open={openBackdrop} className="backdrop">
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
    );
  }
}

export default CallsheetView;
